import type { HelpTopic } from '../types';

export const mappingTopics: HelpTopic[] = [
  {
    id: 'wms.mapping.sachivalayam',
    title: 'Map Sachivalayams to a Work',
    icon: 'ri-government-fill',
    summary: 'Associate one or many sachivalayams that a work is executed in — drives ward-level reporting and access for field users.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              A work is usually executed in a specific sachivalayam (ward-secretariat). A linear work — like a pipeline or
              road — can cross several. Each link records the sachivalayam plus the share of the work's scope (length /
              area / beneficiaries).
            </p>
          </div>
        )
      },
      {
        id: 'single-work',
        title: 'Map sachivalayams on one work',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Select a row in the works grid.</li>
            <li>Click <span className="font-medium">Map Sachivalayams</span> — the selector opens with map + list.</li>
            <li>Pick one or more sachivalayams (search or click on the map).</li>
            <li>Set remarks / share if the work spans multiple; Save.</li>
          </ol>
        )
      },
      {
        id: 'multi-work',
        title: 'Same sachivalayam → many works',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Multi-select works in the grid (checkboxes).</li>
            <li>Use the bulk <span className="font-medium">Map Sachivalayams</span> action from the selection bar.</li>
            <li>Pick the sachivalayams once — they're applied to every selected work.</li>
          </ol>
        )
      },
      {
        id: 'from-gis',
        title: 'From the GIS Mapping page',
        body: (
          <p className="text-sm text-gray-700">
            The same sachivalayam-mapping flow is available from <span className="font-medium">Planning → GIS Mapping</span>
            whenever you're already geo-locating the work. Changes sync in both places.
          </p>
        )
      }
    ],
    related: ['wms.mapping.gis'],
    pageLinks: [
      { label: 'Open Sachivalayam Mapping', to: '/app/ds-wms/progress/sachivalayam-mapping', icon: 'ri-government-fill' },
      { label: 'Open GIS Mapping', to: '/app/ds-wms/planning/gis-mapping', icon: 'ri-road-map-line' }
    ]
  },
  {
    id: 'wms.mapping.gis',
    title: 'GIS Mapping — Geo-code & Link Assets',
    icon: 'ri-road-map-line',
    summary: 'Place a work on the map, assign sachivalayams, and link the existing roads / water bodies / land parcels it touches.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p className="text-sm text-gray-700">
            The GIS Mapping page is the one-stop geocoding workspace. A work can be pinned to a point or drawn as a line
            /polygon; once placed, it can be linked to the underlying UDA GIS assets: sachivalayams, roads, water bodies
            and land parcels. This enriches reports, dashboards and the Digital Twin viewer.
          </p>
        )
      },
      {
        id: 'navigate',
        title: 'Navigating the page',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open <span className="font-medium">Planning → GIS Mapping</span>.</li>
            <li>UDA summary view shows mapped vs unmapped counts — click a UDA to drill into its works.</li>
            <li>Use the <span className="font-medium">Total / GIS Mapped / GIS Unmapped</span> tabs to filter.</li>
            <li>Filters for FY / Work Type / Category / Nature narrow the list further.</li>
          </ol>
        )
      },
      {
        id: 'geo-code',
        title: 'Geo-code a work',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Select the work row.</li>
            <li>Click <span className="font-medium">Map on GIS</span> — the map tool opens.</li>
            <li>Pick geometry type: <span className="font-medium">point</span>, <span className="font-medium">line</span>, or <span className="font-medium">polygon</span>.</li>
            <li>Draw on the base map (or paste lat/lng / GeoJSON); Save.</li>
          </ol>
        )
      },
      {
        id: 'assign-sachivalayams',
        title: 'Assign sachivalayams',
        body: (
          <p className="text-sm text-gray-700">
            Same selector as the <span className="font-medium">Sachivalayam Mapping</span> page. Multi-select works are
            supported. When the work's geometry is already drawn, the tool can auto-suggest overlapping sachivalayams.
          </p>
        )
      },
      {
        id: 'link-assets',
        title: 'Link existing roads / water bodies / land parcels',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              After geocoding, pick which existing GIS features the work covers. Use this for maintenance, widening,
              relaying, or improvement works where the "subject" is an asset the UDA already tracks.
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Select the work → open <span className="font-medium">Linked Assets</span> panel.</li>
              <li>Click <span className="font-medium">Add Road</span> / <span className="font-medium">Add Water Body</span> / <span className="font-medium">Add Parcel</span>.</li>
              <li>Search by code / name, or click features on the map.</li>
              <li>Save — linked assets appear in the work profile and dashboards.</li>
            </ol>
          </div>
        )
      },
      {
        id: 'tips',
        title: 'Tips',
        body: (
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li>For pipeline / cable works, use a line geometry — it gives accurate length for BOQ verification.</li>
            <li>Auto-suggested sachivalayams use geometric intersection; verify before confirming.</li>
            <li>Linking a land parcel is useful for acquisition / compensation works.</li>
          </ul>
        )
      }
    ],
    related: ['wms.mapping.sachivalayam', 'wms.progress.physical-milestones'],
    pageLinks: [
      { label: 'Open GIS Mapping', to: '/app/ds-wms/planning/gis-mapping', icon: 'ri-road-map-line' },
      { label: 'Open Sachivalayam Mapping', to: '/app/ds-wms/progress/sachivalayam-mapping', icon: 'ri-government-fill' }
    ]
  }
];
